import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  linkedSignal,
  signal,
  untracked,
} from '@angular/core';
import { Router } from '@angular/router';
import { catchError, forkJoin, firstValueFrom, of } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { ButtonComponent } from '@abook-ui/components/button/button.component';
import { BasketStore } from '../../stores/basket.store';
import { BasketItemComponent } from '../basket-item/basket-item.component';
import { CheckoutService, PeriodAvailability } from '@features/checkout/checkout.service';
import { blockingBasketMaxBookingTime } from '../../max-booking-time';
import { CheckoutStore } from '@features/checkout/checkout.store';
import { JourneyStore } from '@features/checkout/journey.store';
import { CheckoutPeriodInput, CustomerJourneyType } from '@features/checkout/models/checkout.model';
import { SiteStore } from '@core/site/site.store';
import { AuthenticationService } from '@core/authentication/authentication.service';
import { DateService } from '@core/services/date.service';
import { LoginRequiredDialogComponent } from '../login-required-dialog/login-required-dialog.component';

@Component({
  selector: 'app-basket',
  imports: [MatIconModule, BasketItemComponent, ButtonComponent],
  templateUrl: './basket.component.html',
  styleUrl: './basket.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { '[class.basket--compact]': 'compact()' },
})
export class BasketComponent {
  readonly compact = input<boolean>(false);

  protected readonly basket = inject(BasketStore);
  private readonly checkoutService = inject(CheckoutService);
  private readonly checkoutStore = inject(CheckoutStore);
  private readonly journeyStore = inject(JourneyStore);
  private readonly router = inject(Router);
  private readonly site = inject(SiteStore);
  private readonly auth = inject(AuthenticationService);
  private readonly dateService = inject(DateService);
  private readonly dialog = inject(MatDialog);

  readonly isCheckingOut = signal(false);

  /**
   * Kurvens indhold, som fejlmeldinger måles imod: id, tid og lokale. Bevidst *uden*
   * isAvailable/message, så de markeringer checkout selv sætter ikke tæller som en ændring.
   */
  private readonly basketItemsKey = computed(() =>
    this.basket
      .items()
      .map((item) => `${item.id}:${item.start}:${item.end}:${item.resourceId}`)
      .join('|'),
  );

  /**
   * Hvorfor kurven ikke kan gå videre — fx at maksimumtiden for lokalerne er brugt op. Vises som
   * en bjælke under betalingsknappen, samme sted og samme form som kurven i administrationen.
   * <p>
   * Nulstilles af sig selv, så snart kurven ændrer sig: en rettet tid eller en fjernet linje må
   * ikke stå tilbage med en besked om et problem, brugeren netop har løst. Beskeden kommer igen,
   * når man går videre og tiderne bliver tjekket på ny.
   */
  readonly checkoutError = linkedSignal<string | null>(() => {
    this.basketItemsKey();
    return null;
  });

  constructor() {
    // Samme oprydning på selve kurvlinjerne: "Ikke tilgængelig" hører til det tjek, den kom fra.
    effect(() => {
      this.basketItemsKey();
      untracked(() => this.basket.clearAvailabilityMarks());
    });
  }

  async goToCheckout(): Promise<void> {
    const items = this.basket.combinedItems();
    if (!items.length) return;

    // Går man videre, starter vi forfra: beskeden sættes kun igen af tjekkene nedenfor.
    this.checkoutError.set(null);

    if (!this.auth.isAuthenticated()) {
      this.dialog.open(LoginRequiredDialogComponent, { width: '400px', data: { returnUrl: '/basket' } });
      return;
    }

    this.isCheckingOut.set(true);
    try {
      // Reset stale availability state before the new checks speak
      this.basket.clearAvailabilityMarks();

      const userId = this.auth.user()?.id ?? null;
      const existingBookingId = this.checkoutStore.bookingId();

      // Check availability for the combined (merged) period — one check per group
      const checks = await firstValueFrom(
        forkJoin(
          items.map((item) =>
            this.checkoutService
              .checkPeriodAvailability(
                this.dateService.toApiString(item.start),
                this.dateService.toApiString(item.end),
                item.resourceId,
                userId,
                item.activityId,
                existingBookingId,
                item.bookingMode,
              )
              .pipe(catchError(() => of<PeriodAvailability>({ isAvailable: false, canOverride: false, message: 'Tilgængelighed kunne ikke kontrolleres', price: 0, maxBookingTime: null }))),
          ),
        ),
      );

      const unavailable = checks.some((r) => !r.isAvailable);
      if (unavailable) {
        checks.forEach((result, i) => {
          if (!result.isAvailable) {
            // Mark all constituent slots so store state is consistent after the combined-item check
            items[i].slotIds.forEach((id) =>
              this.basket.updateItemAvailability(id, false, result.message ?? 'Ikke tilgængelig'),
            );
          }
        });
        return;
      }

      // Ledighedstjekket fortæller også om maksimumtiden. Sig det her, hvor tiderne stadig er på
      // skærmen — og luk vejen videre: at overrule reglen er administrationens sag, ikke borgerens.
      // Kurvens egne tider lægges sammen: hver for sig kan de være under kvoten og tilsammen over,
      // og det kan serveren ikke se, før perioderne er oprettet.
      const overQuota = blockingBasketMaxBookingTime(checks, (s) => this.dateService.parse(s));
      if (overQuota) {
        this.checkoutError.set(overQuota);
        return;
      }

      const existingId = this.checkoutStore.bookingId();
      if (existingId) {
        try {
          await firstValueFrom(this.checkoutStore.clearBooking(existingId));
        } catch {
          // Stale booking deletion failed — booking already deleted, server error,
          // or booking has periods in a non-new status (API rejects delete in that case).
          // Proceed — initCheckout creates a fresh booking regardless.
        }
      }

      const availabilityType = items[0]?.availabilityType ?? 'BOOKING';
      const journeyType =
        availabilityType === 'REQUEST'
          ? CustomerJourneyType.RESERVATION
          : this.basket.totalPrice() === 0
          ? CustomerJourneyType.FREE_BOOKING
          : CustomerJourneyType.BOOKING;

      this.journeyStore.setJourney(journeyType);
      this.journeyStore.setJourneyAvailabilityType(availabilityType);

      const periods: CheckoutPeriodInput[] = items.map((item) => ({
        resourceId: item.resourceId,
        activityId: item.activityId,
        start: this.dateService.toApiString(item.start),
        end: this.dateService.toApiString(item.end),
        price: item.price,
      }));
      await this.checkoutStore.initCheckout(periods, journeyType, items[0]?.bookingMode);

      if (!this.checkoutStore.booking()) {
        // En afvisning med en begrundelse kan brugeren selv rette: bliv i kurven og markér tiderne,
        // som når en tid er optaget. Uden en begrundelse er der intet at handle på, og fejlsiden er
        // det rigtige sted.
        const reason = this.checkoutStore.errorMessage();
        if (reason) {
          this.checkoutError.set(reason);
          return;
        }

        this.site.closeRightDrawer();
        await this.router.navigate(['/checkout/error']);
        return;
      }

      this.site.closeRightDrawer();
      await this.router.navigate(['/checkout/addons']);
    } finally {
      this.isCheckingOut.set(false);
    }
  }
}
